import {
  ADVICE_NAMES,
  Advice,
  AdviceCoverageName,
  InsuranceAdvice,
  PurchaseRecommendation,
} from "Store/advice/interfaces";
import {
  CriticalIllnessCoverage,
  DeathCoverage,
  DisabilityCoverage,
} from "Store/interfaces/products/coverage";

import { getCorrectPayment } from "Store/advice/utils";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { isChild, isChildCloseToMaxAge, isInfant } from "Utils/ageUtils";
import { reverseDDMMYYYY } from "Utils/dateUtils";
import { CHILD_MAX_AGE } from "../../LifeProducts/Child/childConstants";
import { AdviceProductDetailsFormValues } from "../ProductDetails/FormElements/interfaces";

interface WeightedProduct {
  readonly weight: number;
}

const defaultWeight: Partial<Record<INSURANCE_NAME_VALUE, number>> = {
  [INSURANCE_NAME_VALUE.DEATH]: 0.51,
  [INSURANCE_NAME_VALUE.DISABILITY_FLEX]: 0.5,
  [INSURANCE_NAME_VALUE.CRITICAL_ILLNESS]: 0.49,
};

function getWeight(
  product: INSURANCE_NAME_VALUE,
  weight: number | undefined,
): number {
  const value = defaultWeight[product];
  const startWeight = value ?? 0.5;
  const addedWeight = weight ?? 0;
  return startWeight + addedWeight;
}

interface Product {
  readonly name: INSURANCE_NAME_VALUE;
  readonly [key: string]: any;
}

export function sortProducts<T extends Product>(
  products: Array<T>,
  weights: Partial<Record<INSURANCE_NAME_VALUE, number>>,
): Array<T> {
  const compareWeightedProduct = (
    productA: WeightedProduct,
    productB: WeightedProduct,
  ): number => productB.weight - productA.weight;

  return products
    .map((product) => ({
      ...product,
      weight: getWeight(product.name, weights[product.name]),
    }))
    .sort(compareWeightedProduct);
}

export const getAdvicesAllowed = (
  adviceValues: Advice,
): [string, InsuranceAdvice][] =>
  Object.entries(adviceValues.advice).filter(
    ([, insuranceAdvice]) =>
      getCorrectPayment(insuranceAdvice).recommendationConfiguration
        .recommendation !== PurchaseRecommendation.NOT_ALLOWED,
  );

export const mapInsuranceNameToAdviceCoverageName = (
  insuranceName: ADVICE_NAMES,
): AdviceCoverageName => {
  switch (insuranceName) {
    case INSURANCE_NAME_VALUE.DEATH:
      return DeathCoverage.DEATH_NOT_SMOKER;
    case INSURANCE_NAME_VALUE.CRITICAL_ILLNESS:
      return CriticalIllnessCoverage.CRITICAL_ILLNESS_NOT_SMOKER;
    case INSURANCE_NAME_VALUE.DISABILITY_FLEX:
    default:
      return DisabilityCoverage.DISABILITY_FLEX;
  }
};

export const mapAdviceCoverageNameToInsuranceName = (
  adviceCoverageName: AdviceCoverageName,
): ADVICE_NAMES => {
  switch (adviceCoverageName) {
    case DeathCoverage.DEATH_NOT_SMOKER:
      return INSURANCE_NAME_VALUE.DEATH;
    case CriticalIllnessCoverage.CRITICAL_ILLNESS_NOT_SMOKER:
      return INSURANCE_NAME_VALUE.CRITICAL_ILLNESS;
    case DisabilityCoverage.DISABILITY_FLEX:
    default:
      return INSURANCE_NAME_VALUE.DISABILITY_FLEX;
  }
};

export const isChildInsuranceRecommended = (
  adviceDetailsFormValues: AdviceProductDetailsFormValues,
): boolean => {
  const childDatesOfBirth = getChildDatesOfBirth(adviceDetailsFormValues);
  return (
    childDatesOfBirth.some((dateOfBirth) =>
      isChild(dateOfBirth, CHILD_MAX_AGE),
    ) || childDatesOfBirth.some(isInfant)
  );
};

export const hasChildCloseToMaxAge = (
  adviceDetailsFormValues: AdviceProductDetailsFormValues,
): boolean =>
  getChildDatesOfBirth(adviceDetailsFormValues).some((dateOfBirth) =>
    isChildCloseToMaxAge(dateOfBirth, CHILD_MAX_AGE),
  );

const getChildDatesOfBirth = (
  adviceDetailsFormValues: AdviceProductDetailsFormValues,
): Date[] =>
  adviceDetailsFormValues.childDatesOfBirth
    ?.map((value) => value.dateOfBirth)
    .map(reverseDDMMYYYY)
    .map((value) => new Date(value)) ?? [];
